import type {CreateExtArgs, DmApis, Extension} from '@wix/document-manager-core'
import type {CompRef, AutoResponsivenessMode} from '@wix/document-services-types'
import {DATA_TYPES} from '../../constants/constants'
import type {VariantsExtensionAPI} from '../variants/variants'
import {AutoResponsiveKey} from './enums'
import type {LayoutSettingsExtensionAPI, AutoResponsivenessValue} from './types'

const createExtension = (): Extension => {
    const createExtensionAPI = ({extensionAPI}: CreateExtArgs): LayoutSettingsExtensionAPI => {
        const variantsApi = () => (extensionAPI as VariantsExtensionAPI).variants
        const getLayoutSettings = (compPointer: CompRef) => {
            return variantsApi().getComponentItemConsideringVariants(compPointer, DATA_TYPES.layoutSettings)
        }

        const updateAutoResponsivenessSettingsByKey = (
            compPointer: CompRef,
            keyToUpdate: AutoResponsiveKey,
            valueToUpdate: AutoResponsivenessValue
        ) => {
            const layoutSettings = variantsApi().getComponentItemConsideringVariants(
                compPointer,
                DATA_TYPES.layoutSettings
            )
            variantsApi().updateComponentDataConsideringVariants(
                compPointer,
                {
                    ...layoutSettings,
                    autoResponsivenessSettings: {
                        ...layoutSettings?.autoResponsivenessSettings,
                        [keyToUpdate]: valueToUpdate
                    },
                    type: 'LayoutSettings'
                },
                DATA_TYPES.layoutSettings
            )
        }

        const removeAutoResponsivenessSettingsByKey = (compPointer: CompRef, keyToRemove: AutoResponsiveKey) => {
            const layoutSettings = getLayoutSettings(compPointer)
            const autoResponsivenessSettings = {...layoutSettings?.autoResponsivenessSettings}
            if (autoResponsivenessSettings?.[keyToRemove] !== undefined) {
                delete autoResponsivenessSettings[keyToRemove]
                variantsApi().updateComponentDataConsideringVariants(
                    compPointer,
                    {
                        ...layoutSettings,
                        autoResponsivenessSettings,
                        type: 'LayoutSettings'
                    },
                    DATA_TYPES.layoutSettings
                )
            }
        }

        return {
            layoutSettings: {
                autoResponsivenessSettings: {
                    mode: {
                        get: (compPointer: CompRef) => getLayoutSettings(compPointer)?.autoResponsivenessSettings?.mode,
                        set: (compPointer: CompRef, mode: AutoResponsivenessMode) =>
                            updateAutoResponsivenessSettingsByKey(compPointer, AutoResponsiveKey.Mode, mode),
                        remove: (compPointer: CompRef) =>
                            removeAutoResponsivenessSettingsByKey(compPointer, AutoResponsiveKey.Mode)
                    },
                    lastGroupingsHash: {
                        get: (compPointer: CompRef) =>
                            getLayoutSettings(compPointer)?.autoResponsivenessSettings?.lastGroupingsHash,
                        set: (compPointer: CompRef, lastGroupingsHash: string) =>
                            updateAutoResponsivenessSettingsByKey(
                                compPointer,
                                AutoResponsiveKey.LastGroupingsHash,
                                lastGroupingsHash
                            ),
                        remove: (compPointer: CompRef) =>
                            removeAutoResponsivenessSettingsByKey(compPointer, AutoResponsiveKey.LastGroupingsHash)
                    },
                    isSynced: {
                        get: (compPointer: CompRef) =>
                            getLayoutSettings(compPointer)?.autoResponsivenessSettings?.isSynced,
                        set: (compPointer: CompRef, isSynced: boolean) =>
                            updateAutoResponsivenessSettingsByKey(compPointer, AutoResponsiveKey.IsSynced, isSynced),
                        remove: (compPointer: CompRef) =>
                            removeAutoResponsivenessSettingsByKey(compPointer, AutoResponsiveKey.IsSynced)
                    }
                }
            }
        }
    }

    const createPublicAPI = ({extensionAPI}: DmApis) => {
        const {layoutSettings} = extensionAPI as LayoutSettingsExtensionAPI
        return {
            layoutSettings: {
                autoResponsivenessSettings: {
                    mode: {
                        get: layoutSettings.autoResponsivenessSettings.mode.get,
                        set: layoutSettings.autoResponsivenessSettings.mode.set,
                        remove: layoutSettings.autoResponsivenessSettings.mode.remove
                    },
                    lastGroupingsHash: {
                        get: layoutSettings.autoResponsivenessSettings.lastGroupingsHash.get,
                        set: layoutSettings.autoResponsivenessSettings.lastGroupingsHash.set,
                        remove: layoutSettings.autoResponsivenessSettings.lastGroupingsHash.remove
                    },
                    isSynced: {
                        get: layoutSettings.autoResponsivenessSettings.isSynced.get,
                        set: layoutSettings.autoResponsivenessSettings.isSynced.set,
                        remove: layoutSettings.autoResponsivenessSettings.isSynced.remove
                    }
                }
            }
        }
    }

    return {
        name: 'layoutSettings',
        createExtensionAPI,
        createPublicAPI
    }
}

export {createExtension}
